import React, { useState, useEffect } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, FlatList,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useToast } from 'react-native-toast-notifications';
import { useGame } from '../../context/GameContext';
import { SCREEN_NAMES } from '../../utils/constant';
import { theme } from '../Common.style';

export default function GameVotingScreen() {
  const navigation = useNavigation();
  const toast = useToast();
  const {
    room, currentRound, currentUserId, castVote, hasVoted,
    onlinePlayers, localPlayers, castLocalVote,
  } = useGame();

  const [selected, setSelected] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localVoterIdx, setLocalVoterIdx] = useState(0);

  useEffect(() => {
    if (!room || !currentRound) {
      navigation.replace(SCREEN_NAMES.GAME_LOBBY);
    } else if (currentRound.status === 'IMPOSTOR_GUESS') {
      navigation.navigate(SCREEN_NAMES.GAME_IMPOSTOR_GUESS);
    } else if (currentRound.status === 'FINISHED') {
      navigation.navigate(SCREEN_NAMES.GAME_RESULT);
    }
  }, [room?.id, currentRound?.status]);

  if (!room || !currentRound || currentRound.status === 'IMPOSTOR_GUESS' || currentRound.status === 'FINISHED') {
    return null;
  }

  const isLocal = room.mode === 'LOCAL';
  const players = isLocal ? localPlayers : onlinePlayers;
  const votes = currentRound.votes ?? [];
  const totalVotes = votes.length;
  const totalPlayers = players.length;

  const handleConfirm = async () => {
    if (selected === null) return;
    try {
      setLoading(true);
      if (isLocal) {
        castLocalVote(localVoterIdx, selected);
        const nextVoter = localVoterIdx + 1;
        setSelected(null);
        if (nextVoter >= totalPlayers) {
          navigation.navigate(SCREEN_NAMES.GAME_IMPOSTOR_GUESS);
        } else {
          setLocalVoterIdx(nextVoter);
        }
      } else {
        setConfirmed(true); // lock UI immediately before any async/subscription update
        await castVote(selected);
      }
    } catch (e) {
      setConfirmed(false);
      toast.show(e.message || 'Erreur', { type: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  const alreadyDone = !isLocal && (hasVoted || confirmed);

  const playerName = (idx) => {
    if (isLocal) return localPlayers[idx]?.name ?? `Joueur ${idx + 1}`;
    const p = players[idx];
    return p?.user?.firstName ?? 'Joueur';
  };

  const playerId = (item, idx) => isLocal ? idx : item.userId;

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Qui est l'Imposteur ?</Text>

      {isLocal && (
        <Text style={styles.passPrompt}>
          Vote de {localPlayers[localVoterIdx]?.name ?? `Joueur ${localVoterIdx + 1}`}
        </Text>
      )}

      <Text style={styles.progress}>{totalVotes}/{totalPlayers} ont voté</Text>

      <FlatList
        data={players}
        keyExtractor={(_, i) => String(i)}
        numColumns={2}
        columnWrapperStyle={styles.row}
        style={styles.list}
        renderItem={({ item, index }) => {
          const pid = playerId(item, index);
          const name = isLocal ? (item.name ?? `J${index}`) : (item.user?.firstName ?? 'J');
          const voteCount = votes.filter((v) => v.targetId === pid).length;
          const isSelected = selected === pid;
          const isSelf = !isLocal && item.userId === currentUserId;

          return (
            <TouchableOpacity
              style={[
                styles.card,
                isSelected && styles.cardSelected,
                isSelf && styles.cardSelf,
                alreadyDone && styles.cardDisabled,
              ]}
              onPress={() => !alreadyDone && !isSelf && setSelected(pid)}
              disabled={alreadyDone || isSelf}
            >
              <View style={[styles.avatar, isSelected && styles.avatarSelected]}>
                <Text style={styles.avatarText}>{name[0]?.toUpperCase()}</Text>
              </View>
              <Text style={[styles.cardName, isSelected && styles.cardNameSelected]}>{name}</Text>
              {voteCount > 0 && (
                <View style={styles.voteBadge}>
                  <Text style={styles.voteBadgeText}>{voteCount}</Text>
                </View>
              )}
            </TouchableOpacity>
          );
        }}
      />

      {!alreadyDone && (
        <View style={styles.footer}>
          <TouchableOpacity
            style={[styles.confirmBtn, (selected === null || loading) && styles.btnDisabled]}
            onPress={handleConfirm}
            disabled={selected === null || loading}
          >
            <Text style={styles.confirmBtnText}>
              {loading ? 'Envoi…' : 'Confirmer mon vote'}
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {alreadyDone && (
        <View style={styles.footer}>
          <Text style={styles.waitText}>Vote enregistré — en attente des autres…</Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#fff' },
  title: { fontSize: 24, fontWeight: '800', textAlign: 'center', marginTop: 20, marginBottom: 4 },
  passPrompt: { textAlign: 'center', color: theme.colors.primary, fontWeight: '600', marginBottom: 4 },
  progress: { textAlign: 'center', color: '#888', marginBottom: 16 },
  list: { flex: 1, paddingHorizontal: 12 },
  row: { justifyContent: 'space-between', marginBottom: 12 },
  card: { flex: 0.48, alignItems: 'center', borderRadius: 16, borderWidth: 2, borderColor: '#eee', padding: 16 },
  cardSelected: { borderColor: theme.colors.primary, backgroundColor: theme.colors.primary + '15' },
  cardSelf: { opacity: 0.4 },
  cardDisabled: { opacity: 0.7 },
  avatar: { width: 56, height: 56, borderRadius: 28, backgroundColor: '#ddd', alignItems: 'center', justifyContent: 'center', marginBottom: 8 },
  avatarSelected: { backgroundColor: theme.colors.primary },
  avatarText: { color: '#fff', fontWeight: '700', fontSize: 20 },
  cardName: { fontWeight: '600', fontSize: 15, color: '#333' },
  cardNameSelected: { color: theme.colors.primary },
  voteBadge: { position: 'absolute', top: 8, right: 8, backgroundColor: '#e53935', borderRadius: 10, width: 20, height: 20, alignItems: 'center', justifyContent: 'center' },
  voteBadgeText: { color: '#fff', fontSize: 11, fontWeight: '700' },
  footer: { padding: 20, borderTopWidth: 1, borderTopColor: '#eee' },
  confirmBtn: { backgroundColor: theme.colors.primary, borderRadius: 14, paddingVertical: 16, alignItems: 'center' },
  btnDisabled: { opacity: 0.4 },
  confirmBtnText: { color: '#fff', fontWeight: '700', fontSize: 17 },
  waitText: { textAlign: 'center', color: '#888', fontSize: 15 },
});
